import React from "react";
import {Localizer} from "@/main/react/Types";
import { MAIN_PAGE_LIST_WIDTH_CLASS, MainPageListStack } from "@/main/react/list/MainPageListLayout";
import {PaginatedListWrapperV2} from "@/main/react/list/PaginatedListWrapperV2";
import {createStandardPaginatedStatusSlots, PaginatedListEmptyState} from "@/main/react/list/PaginatedListState";
import DateFormatter from "@/main/common/DateFormatter";
import {CtaButton} from "@/main/react/buttons/CtaButton";
import {Check, Inbox, X} from "lucide-react";
import {showRejectJoinRequestDialog} from "@/main/react/Dialog/RejectJoinRequestDialog";
import {toast} from "sonner";
import ProfileImage from "@/main/react/buttons/ProfileImage";
import {emitDiscussionAddToGroupRequestResolved, emitGroupAddedToDiscussion} from "@/main/common/EventRegistry";
import {ClientFactory} from "@/main/common/ClientFactory";
import { getSafeToastMessageFromError, logApiFailure } from "@/main/common/ApiUtils";
import {DiscussionRequestToAddToGroupDTO, GroupDTO} from "@/generated-client";
import { hasDisplayUsername } from "@/main/common/UsernameDisplayUtils";

type Props = {
    discussionPublicId: string;
    localizer: Localizer;
    removedRequestIds?: Set<string>;
};

export default function DiscussionRequestsList(props: Props) {
    const {discussionPublicId, localizer, removedRequestIds = new Set()} = props;
    const paginationApi = React.useMemo(() => ClientFactory.getAjaxPaginationRestControllerApi(), []);
    const discussionApi = React.useMemo(() => ClientFactory.getAjaxDiscussionRestControllerApi(), []);

    const fetchPage = React.useCallback(async (cutoffCreatedAt?: string) => {
        try {
            const { data } = await paginationApi.requestsToAddDiscussionToGroup(discussionPublicId, cutoffCreatedAt);
            return data ?? [];
        } catch (e) {
            logApiFailure("[DiscussionRequestsList] fetch discussion requests error", e);
            throw e;
        }

    }, [discussionPublicId, paginationApi]);

    const getId = React.useCallback((item: DiscussionRequestToAddToGroupDTO) => item?.requestPublicId ?? undefined, []);

    return (
        <PaginatedListWrapperV2<DiscussionRequestToAddToGroupDTO, string>
            key={`discussion:requests:${discussionPublicId}`}
            fetchPage={fetchPage}
            getId={getId}
            className={MAIN_PAGE_LIST_WIDTH_CLASS}
            getVisibleItemsCount={(items) => items.filter(i => i.requestPublicId && !removedRequestIds.has(i.requestPublicId)).length}
            statusSlots={createStandardPaginatedStatusSlots({
                loadingMessage: localizer("home.loading"),
                endMessage: localizer("home.end-of-list"),
                errorMessage: localizer("paginated.load_failed"),
                retryLabel: localizer("literal.retry"),
                empty: (
                    <PaginatedListEmptyState
                        icon={Inbox}
                        title={localizer("discussion.section.requests")}
                        description={localizer("discussion.requests.empty")}
                    />
                )
            })}
            render={({items}) => {
                const filteredItems = items.filter(i => i.requestPublicId && !removedRequestIds.has(i.requestPublicId));
                return (
                    <MainPageListStack className="gap-3">
                        {filteredItems.map(item => (
                            <DiscussionRequestCard
                                key={getId(item)}
                                item={item}
                                localizer={localizer}
                                onApprove={async () => {
                                    const { data: resolvedIds } = await discussionApi.approveAddToGroupRequest(String(item.requestPublicId));
                                    const ids = Array.isArray(resolvedIds) && resolvedIds.length > 0
                                        ? resolvedIds
                                        : [String(item.requestPublicId)];
                                    emitDiscussionAddToGroupRequestResolved({discussionPublicId, requestPublicIds: ids});
                                    const group: GroupDTO = {
                                        publicId: item.groupPublicId,
                                        slug: item.groupSlug,
                                        name: item.groupName,
                                        description: item.groupDescription,
                                        thumbnailUrl: item.groupThumbnailUrl,
                                        privateAccess: item.groupPrivateAccess,
                                        createdAt: item.groupCreatedAt,
                                        discussionsCount: item.groupDiscussionsCount,
                                    };
                                    emitGroupAddedToDiscussion({discussionPublicId, group});
                                    toast.success(localizer("discussion.request.approve.success"));
                                }}
                                onReject={() => {
                                    showRejectJoinRequestDialog({
                                        localizer,
                                        username: item.username ?? (localizer("notifications.someone")),
                                        title: localizer("discussion.request.reject.title"),
                                        message: localizer("discussion.request.reject.message", item.username ?? ""),
                                        yesLabel: localizer("discussion.request.reject"),
                                        noLabel: localizer("discussion.request.reject.no"),
                                        // The shared reject dialog hardcoded the JOIN-REQUEST failure
                                        // copy until 2026-09-09, so a failed rejection here told a post
                                        // author their join request could not be rejected, on a screen
                                        // where no join request exists.
                                        failureMessage: localizer("discussion.request.reject.failed"),
                                        onConfirm: async (resolutionNote?: string) => {
                                            await discussionApi.rejectAddToGroupRequest(String(item.requestPublicId), {
                                                resolutionNote: resolutionNote ?? undefined,
                                            });
                                            emitDiscussionAddToGroupRequestResolved({discussionPublicId, requestPublicIds: [String(item.requestPublicId)]});
                                            toast.success(localizer("discussion.request.reject.success"));
                                        },
                                    });
                                }}
                            />
                        ))}
                    </MainPageListStack>
                );
            }}
        />
    );
}

function DiscussionRequestCard(props: {
    item: DiscussionRequestToAddToGroupDTO;
    localizer: Localizer;
    onApprove: () => Promise<void>;
    onReject: () => void;
}) {
    const {item, localizer, onApprove, onReject} = props;
    const [busy, setBusy] = React.useState(false);
    const df = new DateFormatter(localizer);

    const handleApprove = async () => {
        if (busy) return;
        setBusy(true);
        try {
            await onApprove();
        } catch (e: any) {
            logApiFailure("Approve discussion request failed", e);
            toast.error(getSafeToastMessageFromError(e, localizer("discussion.request.approve.failed")));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="w-full rounded-2xl border-1 border-border shadow-md p-3 flex flex-col gap-3">
            <div className="flex items-start gap-3">
                {item.groupThumbnailUrl ? (
                    <ProfileImage localizer={localizer}
                        imageUrl={item.groupThumbnailUrl}
                        username={item.groupName ?? ""}
                        mode="group"
                        hoverable={false}
                        className="size-16"
                    />
                ) : null}
                <div className="flex-1 min-w-0">
                    <div className="font-semibold text-base">
                        <a className="standard-link" href={`/group/${item.groupSlug ?? item.groupPublicId ?? ""}`}>
                            {item.groupName ?? (localizer("group.settings.unnamed"))}
                        </a>
                    </div>
                    <div className="text-xs text-neutral-text mt-1">
                        {(localizer("discussion.request.from"))} <span translate={hasDisplayUsername(item.username) ? "no" : undefined} className="wrap-anywhere font-medium">{item.username ?? (localizer("notifications.someone"))}</span>
                        {item.requestCreatedAt ? ` · ${df.timeSince(item.requestCreatedAt)}` : ""}
                    </div>
                    <p className="text-sm mt-2 wrap-anywhere">{item.requestNote ?? (localizer("discussion.request.no_note"))}</p>
                </div>
            </div>
            <div className="flex items-center justify-end gap-2">
                <CtaButton
                    onClick={onReject}
                    icon={X}
                    text={localizer("discussion.request.reject")}
                    variant="destructive"
                    disabled={busy}
                />
                <CtaButton
                    onClick={handleApprove}
                    icon={Check}
                    text={localizer("discussion.request.approve")}
                    variant="primary"
                    disabled={busy}
                    isLoading={busy}
                />
            </div>
        </div>
    );
}
